<template>
  <div class="page-wrapper export-center">
    <div class="page-title">
      <CIcon name="cil-cloud-download" /> Export Center
    </div>
    <CRow>
      <CCol md="12">
        <CCard>
          <CCardHeader>
            <div class="d-flex justify-content-between align-items-center">
              <h4 class="mb-0">Export Center</h4>
              <CBadge color="primary" class="p-2" v-if="$store.state.user">Role: {{ $store.state.user.role }}</CBadge>
            </div>
          </CCardHeader>
          <CCardBody>
            <CRow>
              <CCol md="3">
                <CSelect
                  label="Select Date Type"
                  :options="timeTypeRadio"
                  :value.sync="selected.timeType"
                />
              </CCol>
              <CCol md="4">
                <CInput
                  label="From Date"
                  type="date"
                  v-model="selected.fromDate"
                />
              </CCol>
              <CCol md="4">
                <CInput
                  label="To Date"
                  type="date"
                  v-model="selected.toDate"
                />
              </CCol>
            </CRow>

            <CRow class="mt-3">
              <CCol md="4">
                <label>Filter by Zone</label>
                <multiselect
                  v-model="selected.zone"
                  :options="options.zones"
                  :multiple="true"
                  :close-on-select="false"
                  placeholder="Select zones"
                />
              </CCol>
              <CCol md="4">
                <CSelect
                  label="Filter by Outcome"
                  :options="options.outcomes"
                  placeholder="All Outcomes"
                  :value.sync="selected.outcome"
                />
              </CCol>
              <CCol md="4">
                <CSelect
                  label="Filter by Species Class"
                  :options="options.class"
                  placeholder="All Classes"
                  :value.sync="selected.class"
                />
              </CCol>
            </CRow>
            <CRow class="mt-3">
              <CCol md="4">
                <label>Filter by Approval Status</label>
                <multiselect
                  v-model="selected.approvalStatus"
                  :options="options.approvalStatuses"
                  :multiple="true"
                  :close-on-select="false"
                  placeholder="Select statuses"
                />
              </CCol>
              <CCol md="4">
                <label>Filter by Unit (Project)</label>
                <multiselect
                  v-model="selected.unit"
                  :options="options.units"
                  :multiple="true"
                  :close-on-select="false"
                  placeholder="Select units"
                />
              </CCol>
              <CCol md="4">
                <label>Filter by Creator Role</label>
                <multiselect
                  v-model="selected.role"
                  :options="options.roles"
                  :multiple="true"
                  :close-on-select="false"
                  placeholder="Select roles"
                />
              </CCol>
            </CRow>

            <CRow class="mt-4">
              <CCol md="12">
                <div class="p-4 bg-light rounded border column-config">
                  <h5 class="mb-3"><CIcon name="cil-columns" class="mr-2"/>Configure Export Columns</h5>
                  <div class="d-flex flex-wrap column-grid">
                    <div v-for="(label, key) in availableColumns" :key="key" class="column-item p-2">
                        <CInputCheckbox
                            :label="label"
                            :checked="selectedColumns.includes(key)"
                            @update:checked="(val) => toggleColumn(key, val)"
                        />
                    </div>
                  </div>
                </div>
              </CCol>
            </CRow>

            <CRow class="mt-5 text-center">
              <CCol sm="12">
                <div class="mb-4" v-if="loading">
                  <pulse-loader :loading="loading" color="#2E7D32"></pulse-loader>
                  <p class="text-muted mt-2">Preparing your data, please wait...</p>
                </div>
                <div class="d-flex justify-content-center flex-wrap gap-3">
                  <CButton color="success" size="lg" class="px-4 py-2 shadow-sm" @click="fetchAndExport('excel')" :disabled="loading">
                    <CIcon name="cil-cloud-download" class="mr-2"/>Export as Excel
                  </CButton>
                  <CButton color="danger" size="lg" class="px-4 py-2 shadow-sm" @click="fetchAndExport('pdf')" :disabled="loading">
                    <CIcon name="cil-file" class="mr-2"/>Export as PDF
                  </CButton>
                  <CButton color="secondary" variant="outline" size="lg" class="px-4 py-2" @click="resetFilters">
                    Reset Filters
                  </CButton>
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Hidden JsonExcel component for Excel export -->
    <json-excel
      ref="excelDownload"
      v-show="false"
      :data="exportData"
      :fields="excelFields"
      :name="excelFilename"
    />
  </div>
</template>

<script>
import axios from 'axios';
import store from '../store';
import PulseLoader from 'vue-spinner/src/PulseLoader.vue';
import JsonExcel from 'vue-json-excel';
import Multiselect from 'vue-multiselect';
import { jsPDF } from "jspdf";

export default {
  name: 'ExportCenter',
  components: {
    PulseLoader,
    JsonExcel,
    Multiselect
  },
  data() {
    return {
      baseURL: store.state.baseURL,
      loading: false,
      exportData: [],
      options: {
        zones: [],
        outcomes: [],
        class: [],
        roles: [],
        units: [],
        approvalStatuses: []
      },
      selected: {
        timeType: 'admission',
        fromDate: '',
        toDate: '',
        zone: [],
        outcome: null,
        class: null,
        approvalStatus: [],
        unit: [],
        role: []
      },
      timeTypeRadio: [
        { value: 'admission', label: 'Date of Admission' },
        { value: 'outcome', label: 'Date of Outcome' }
      ],
      selectedColumns: [],
      allPossibleColumns: {
        caseNumber: 'Case ID',
        'animalData.identityName': 'Species',
        zoneName: 'Zone',
        dateOfAdmission: 'Adm Date',
        dateOfOutcome: 'Out Date',
        finalDisposition: 'Outcome',
        placeOfRescue: 'Rescue Place',
        sex: 'Sex',
        stage: 'Stage',
        weight: 'Weight',
        length: 'Length',
        displacement: 'Cause',
        rescuedBy: 'Rescued By',
        broughtBy: 'Brought By',
        remarks: 'Remarks',
        outcomeNotes: 'Outcome Notes',
        gpsLatitudeRescue: 'Lat',
        gpsLongditudeRescue: 'Long'
      },
      sensitiveFields: ['remarks', 'gpsLatitudeRescue', 'gpsLongditudeRescue', 'rescuedBy', 'broughtBy']
    }
  },
  computed: {
    availableColumns() {
      const role = store.state.user ? store.state.user.role : 'Viewer';
      const cols = { ...this.allPossibleColumns };
      if (role === 'Viewer') {
        this.sensitiveFields.forEach(f => delete cols[f]);
      }
      return cols;
    },
    excelFields() {
      const result = {};
      this.selectedColumns.forEach(key => {
        result[this.allPossibleColumns[key]] = key;
      });
      return result;
    },
    excelFilename() {
      return `SaveAnimals_Export_${new Date().toISOString().split('T')[0]}.xls`;
    }
  },
  mounted() {
    this.getFilters();
    this.selectedColumns = Object.keys(this.availableColumns);
  },
  methods: {
    getFilters() {
      axios({
        url: this.baseURL + "records/filters",
        headers: { authorization: store.state.token },
        method: "POST"
      }).then(res => {
        if (res.data.status) {
          this.options.zones = res.data.data.zones;
          this.options.outcomes = res.data.data.outcomes.map(o => ({ value: o, label: o }));
          this.options.class = res.data.data.classes.map(c => ({ value: c, label: c }));
          this.options.roles = res.data.data.roles;
          this.options.units = res.data.data.units;
          this.options.approvalStatuses = res.data.data.approvalStatuses;
        }
      });
    },
    toggleColumn(key, checked) {
      if (checked) {
        if (!this.selectedColumns.includes(key)) this.selectedColumns.push(key);
      } else {
        this.selectedColumns = this.selectedColumns.filter(c => c !== key);
      }
    },
    resetFilters() {
      this.selected = {
        timeType: 'admission',
        fromDate: '',
        toDate: '',
        zone: [],
        outcome: null,
        class: null,
        approvalStatus: [],
        unit: [],
        role: []
      };
      this.selectedColumns = Object.keys(this.availableColumns);
    },
    fetchAndExport(format) {
      if (!this.selected.fromDate || !this.selected.toDate) {
        this.$notify({ group: 'notification', type: 'error', title: 'Error', text: 'Please select from and to dates.' });
        return;
      }

      this.loading = true;
      axios({
        url: this.baseURL + "records/export-records",
        headers: { authorization: store.state.token },
        method: "POST",
        data: this.selected
      })
      .then(res => {
        if (res.data.status) {
          this.exportData = res.data.data;
          if (format === 'excel') {
            setTimeout(() => {
              if (this.$refs.excelDownload && typeof this.$refs.excelDownload.generate === 'function') {
                this.$refs.excelDownload.generate();
              } else {
                this.$notify({ group: 'notification', type: 'warn', title: 'Export', text: 'Excel generator not ready. Please try again.' });
              }
              this.loading = false;
            }, 500);
          } else {
            this.generatePDF();
            this.loading = false;
          }
        } else {
          this.$notify({ group: 'notification', type: 'error', title: 'Error', text: res.data.message });
          this.loading = false;
        }
      })
      .catch(err => {
        console.error(err);
        this.loading = false;
        this.$notify({ group: 'notification', type: 'error', title: 'Error', text: 'Failed to fetch data' });
      });
    },
    generatePDF() {
      const doc = new jsPDF('l', 'mm', 'a4');
      doc.setFontSize(18);
      doc.text("SaveAnimals - Case Summary Report", 14, 22);
      doc.setFontSize(11);
      doc.setTextColor(100);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 30);
      doc.text(`Role: ${store.state.user.role}`, 14, 35);
      
      const headers = this.selectedColumns.map(k => this.allPossibleColumns[k]);
      let y = 50;
      doc.setFontSize(8);
      
      // Basic text-based row generation as fallback for table
      doc.text(headers.join(' | '), 14, y);
      y += 2;
      doc.line(14, y, 280, y);
      y += 5;

      this.exportData.slice(0, 50).forEach(row => {
        const rowData = this.selectedColumns.map(key => {
          let val = '';
          if (key === 'animalData.identityName') val = row.animalData?.identityName || '-';
          else if (key.includes('Date')) val = row[key] ? row[key].substring(0, 10) : '-';
          else val = row[key] || '-';
          return val.toString().substring(0, 15); // Truncate for primitive table
        });
        doc.text(rowData.join(' | '), 14, y);
        y += 5;
        if (y > 190) {
            doc.addPage();
            y = 20;
        }
      });

      if (this.exportData.length > 50) {
          doc.text(`... and ${this.exportData.length - 50} more records. Please use Excel export for complete data.`, 14, y + 10);
      }

      doc.save(`SaveAnimals_Summary_${new Date().toISOString().split('T')[0]}.pdf`);
      this.$notify({ group: 'notification', type: 'success', title: 'Export Ready', text: 'PDF has been generated.' });
    }
  }
}
</script>

<style scoped>
.gap-3 {
  gap: 1rem;
}
.export-center {
  padding: 1rem;
}
.column-config {
  background: #f8f9fa;
  box-shadow: inset 0 2px 4px rgba(0,0,0,0.02);
}
.column-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
}
.column-item {
  border: 1px transparent solid;
  transition: all 0.2s;
}
.column-item:hover {
  background: white;
  border-radius: 4px;
  border-color: #dee2e6;
}
.shadow-sm {
  box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
}
</style>
<style>
/* Multiselect styling */
.multiselect__tags {
  border-radius: 4px;
}
</style>
